import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import useModalStore from "../../../stores/useModalStore";
import {
  XMarkIcon,
  TrashIcon,
  PrinterIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";
import AdminSidebar from "../../../components/AdminSidebar";
import PaginationV2 from "../../../components/PaginationV2";
import QReaderDeviceList from "../../../components/QReaderDeviceList";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import ImportDevicesModal from "../../../components/ImportDevice";
import useDialogStore from "../../../stores/useDialogStore";
import EditableCell from "../../../components/EditableCell";
import { toast } from "react-toastify";
import EditableSelectCell from "../../../components/EditableSelectCell";
import ColumnSearch from "../../../components/ColumnSearch";
import excelIcon from "../../../assets/excel-icon.webp";
import ColumnSelector from "../../../components/ColumnSelector";
import EditableOptionsCell from "../../../components/EditableOptionsCell";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import ActionButtons from "../../../components/ActionButtons";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import Button from "../../../components/Button";
import EditSerialNumberCell from "../../../components/EditSerialNumberCell";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function DeviceStockList() {
  const { showModal, setShowModal, serialNo, setSerialNo } = useModalStore();
  const { openDialog, closeDialog } = useDialogStore();

  const [selectedColumns, setSelectedColumns] = useState({
    unique_id: true,
    brand: true,
    model: true,
    serial_no: true,
    counter_black: true,
    counter_color: true,
    total_counter: true,
    stock: true,
    palette: true,
    created_at: true,
    options: true,
    comment: true,
  });

  const columnOptions = [
    { key: "unique_id", label: "Unique ID" },
    { key: "brand", label: "Brand" },
    { key: "model", label: "Model" },
    { key: "serial_no", label: "Serial No" },
    { key: "counter_black", label: "Counter Black" },
    { key: "counter_color", label: "Counter Color" },
    { key: "stock", label: "Stock" },
    { key: "total_counter", label: "Total Counter" },
    { key: "palette", label: "Palette" },
    { key: "created_at", label: "Date Registered" },
    { key: "comment", label: "Comment" },
    { key: "options", label: "Device Options" },
  ];

  const sortConfig = {
    key: null,
    direction: "desc",
  };

  const user = getUser();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: devicesData,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["devices", location.pathname, location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      // Ensure sort parameters are properly formatted
      const sortBy = params.get("sortBy");
      const order = params.get("order");

      if (sortBy) {
        params.set("sortBy", sortBy);
        params.set("order", order || "asc");
      }

      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/devices?${params.toString()}`
      );
      return response.data;
    },
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    keepPreviousData: false,
    staleTime: 0,
  });

  const deleteDeviceMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.delete(`${window.REACT_APP_API_ENDPOINT}/devices/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "devices",
        location.pathname,
        location.search,
      ]);
      toast.success("Device deleted successfully!");
    },
    onError: (error) => {
      toast.error("Failed to delete device. Please try again.");
      console.error("Delete device error:", error);
    },
  });

  const editCellMutation = useMutation({
    mutationFn: ({ deviceId, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/devices/patch/${deviceId}`,
        { [field]: value, user_id: user.id }
      ),
    onSuccess: () => {
      refetch();
      toast.success("Device updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device. Please try again.");
      console.error("Update device error:", error);
    },
  });

  const exportMutation = useMutation({
    mutationFn: async ({ selectedColumnss, stockTypes }) => {
      const params = new URLSearchParams(location.search);

      // Add stock type parameters if specific types are selected
      if (stockTypes && stockTypes.length > 0) {
        stockTypes.forEach((type) => {
          params.append("type[]", type);
        });
      }

      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/export-excel/all-devices`,
        {
          columns: Object.entries(selectedColumnss)
            .filter(([_, selected]) => selected)
            .map(([key]) => key),
          params: params.toString(),
        },
        {
          responseType: "blob", // This tells Axios to expect a file
        }
      );
      return response;
    },
    onSuccess: (response) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Device_Stock_List_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up

      closeDialog();
      toast.success("Report exported successfully!");
    },
    onError: (error) => {
      console.error("Export error:", error);
      toast.error("Failed to export report. Please try again.");
    },
  });
  const exportByModelMutation = useMutation({
    mutationFn: async ({ selectedColumnss, stockTypes }) => {
      const params = new URLSearchParams(location.search);

      // Add stock type parameters if specific types are selected
      if (stockTypes && stockTypes.length > 0) {
        stockTypes.forEach((type) => {
          params.append("type[]", type);
        });
      }

      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/export-excel/all-devices?export_by_model=true`,
        {
          columns: Object.entries(selectedColumnss)
            .filter(([_, selected]) => selected)
            .map(([key]) => key),
          params: params.toString(),
        },
        {
          responseType: "blob", // This tells Axios to expect a file
        }
      );
      return response;
    },
    onSuccess: (response) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Device_Stock_List_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up

      closeDialog();
      toast.success("Report exported successfully!");
    },
    onError: (error) => {
      console.error("Export error:", error);
      toast.error("Failed to export report. Please try again.");
    },
  });

  useEffect(() => {
    if (serialNo != null) {
      alert("Scanned and filtered.");
      const params = new URLSearchParams(location.search);
      params.set("serial_no", serialNo);
      navigate(`${location.pathname}?${params.toString()}`);
      setSerialNo(null);
    }
  }, [serialNo]);

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page"); // Reset to first page when searching
    navigate(`${location.pathname}?${params.toString()}`);
    // Force refetch when filters change
    queryClient.invalidateQueries(["devices"]);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    // If clicking the same column, toggle direction
    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      // If clicking a new column, set it to ascending
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    // Ensure we're using the correct parameter names for the API
    const sortBy = params.get("sortBy");
    const order = params.get("order");

    // Update the URL with the correct parameters
    const newParams = new URLSearchParams();
    if (sortBy) newParams.set("sortBy", sortBy);
    if (order) newParams.set("order", order);

    // Preserve other existing parameters
    for (const [key, value] of params.entries()) {
      if (key !== "sortBy" && key !== "order") {
        newParams.set(key, value);
      }
    }

    navigate(`${location.pathname}?${newParams.toString()}`);
    queryClient.invalidateQueries(["devices"]);
  };

  const handleClearFilters = () => {
    navigate(location.pathname);
    // Force refetch when filters are cleared
    queryClient.invalidateQueries(["devices"]);
  };

  const handleDelete = (id) => {
    openDialog({
      title: "Delete Device",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg">
            Are you sure you want to delete this device?
            <br />
            <span className="text-red-500 text-sm font-bold">
              (It will be moved to the trash bin)
            </span>
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={<TrashIcon className="h-4 w-4" />}
              onClick={() => {
                deleteDeviceMutation.mutate(id);
                closeDialog();
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ),
    });
  };

  const ColumnSelectorContent = ({ onExport }) => {
    const [localSelectedColumns, setLocalSelectedColumns] =
      useState(selectedColumns);
    const [selectedStockTypes, setSelectedStockTypes] = useState({
      green: true,
      blue: true,
      white: true,
    });

    const handleStockTypeChange = (type) => {
      setSelectedStockTypes((prev) => ({
        ...prev,
        [type]: !prev[type],
      }));
    };

    const getSelectedStockTypes = () => {
      const selected = Object.entries(selectedStockTypes)
        .filter(([_, isSelected]) => isSelected)
        .map(([type]) => type);

      return selected;
    };

    return (
      <div className="bg-white rounded-lg w-full p-4">
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Select Stock Types
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedStockTypes.green}
                onChange={() => handleStockTypeChange("green")}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Green Stock
              </span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedStockTypes.blue}
                onChange={() => handleStockTypeChange("blue")}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Blue Stock
              </span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedStockTypes.white}
                onChange={() => handleStockTypeChange("white")}
                className="rounded border-gray-300 text-gray-600 focus:ring-gray-500"
              />
              <span className="text-sm font-medium text-gray-700">
                White Stock
              </span>
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Select the stock types you want to export
          </p>
        </div>

        {/* Column Selection */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Select Columns
          </h3>
          <ColumnSelector
            columns={columnOptions}
            selectedColumns={localSelectedColumns}
            setSelectedColumns={setLocalSelectedColumns}
          />
        </div>

        <div className="mt-6 flex justify-end space-x-2">
          <Button variant="light" size="sm" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setSelectedColumns(localSelectedColumns); // sync to parent
              onExport(localSelectedColumns, getSelectedStockTypes()); // pass stock types
            }}
          >
            {exportMutation?.isPending ? (
              <div className="flex items-center gap-2">
                <div className="loader h-4 w-4 border-[2px] border-t-transparent border-white rounded-full animate-spin"></div>
                Loading...
              </div>
            ) : (
              "Export"
            )}
          </Button>
        </div>
      </div>
    );
  };

  const handleOpenColumnSelector = () => {
    openDialog({
      title: "Select Stock Types and Columns to Export",
      content: (
        <ColumnSelectorContent
          onExport={(finalColumns, stockTypes) => {
            handleExport(finalColumns, stockTypes); // always fresh
          }}
        />
      ),
    });
  };

  const handleExport = async (selectedColumnss, stockTypes) => {
    if (Object.values(selectedColumnss).some((selected) => selected)) {
      exportMutation.mutate({ selectedColumnss, stockTypes });
    } else {
      toast.warning("Please select at least one column to export");
    }
  };

  const handleExportByModel = async () => {
    // Use the same column selector dialog but with exportByModelMutation
    openDialog({
      title: "Select Stock Types and Columns to Export By Model",
      content: (
        <ColumnSelectorContent
          onExport={(finalColumns, stockTypes) => {
            if (Object.values(finalColumns).some((selected) => selected)) {
              exportByModelMutation.mutate({
                selectedColumnss: finalColumns,
                stockTypes,
              });
            } else {
              toast.warning("Please select at least one column to export");
            }
          }}
        />
      ),
    });
  };

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () =>
      axiosInstance.get(`${window.REACT_APP_API_ENDPOINT}/device-brands`),
    select: (data) => data.data.data.map(({ id, name }) => ({ id, name })),
  }).data;

  const models = useQuery({
    queryKey: ["models"],
    queryFn: () =>
      axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-models?type=all`
      ),
    select: (data) =>
      data.data.data.data.map(({ id, name, device_brand_id }) => ({
        id,
        name,
        device_brand_id,
      })),
  }).data;

  const devices = devicesData?.devices?.data || [];
  const paginationData = {
    current_page: devicesData?.devices?.current_page || 1,
    last_page: devicesData?.devices?.last_page || 1,
    next_page_url: devicesData?.devices?.next_page_url,
    prev_page_url: devicesData?.devices?.prev_page_url,
  };

  const handleOpenImportDialog = () => {
    openDialog({
      title: "Import Devices",
      content: <ImportDevicesModal />,
    });
  };

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
            <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
          </div>
        </div>
        <div className="mt-8 flow-root bg-white rounded-lg">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
                <h4 className="font-semibold text-2xl mb-2 lg:mb-0">
                  Device Stock List
                </h4>
                <div className="flex flex-col gap-2 lg:flex-row items-center">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<XMarkIcon className="h-4 w-4" />}
                    onClick={handleClearFilters}
                    className="mb-2 lg:mb-0"
                  >
                    Clear Filters
                  </Button>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={handleOpenImportDialog}
                    className="mb-2 lg:mb-0"
                    icon={<ArrowUpTrayIcon className="h-4 w-4" />}
                  >
                    Import Devices
                  </Button>
                  <Button
                    variant="outline-orange"
                    size="sm"
                    icon={excelIcon}
                    onClick={handleExportByModel}
                  >
                    Export By Model
                  </Button>
                  <Button
                    variant="outline-success"
                    size="sm"
                    icon={excelIcon}
                    onClick={handleOpenColumnSelector}
                  >
                    Export Device Data
                  </Button>

                  <a
                    href={`/devices/autoform?next_unique_id=${devicesData?.next_unique_id}`}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<PrinterIcon className="h-4 w-4" />}
                    >
                      Register A +
                    </Button>
                  </a>
                  <a
                    href={`/devices/form?next_unique_id=${devicesData?.next_unique_id}`}
                    className="border border-bluePrimary text-bluePrimary rounded-md px-3 py-2 text-sm font-bold"
                  >
                    Register Device +
                  </a>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="lg:min-w-full divide-y divide-gray-300">
                  <thead className="bg-black">
                    <tr>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Unique ID"
                            columnKey="unique_id"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Unique ID..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Brand"
                            columnKey="brand"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Brand..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Model"
                            columnKey="model"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Model..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Serial"
                            columnKey="serial_no"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Serial..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Counter Black"
                            columnKey="counter_black"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Counter Black..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Counter Color"
                            columnKey="counter_color"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Counter Color..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Total Counter"
                            columnKey="total_counter"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Total Counter..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Stock"
                            columnKey="stock"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Stock..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Palette"
                            columnKey="palette"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Palette..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Date Registered"
                            columnKey="created_at"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            isDate={true}
                            placeholder="Search by Date..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        Options
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        Comment
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {isPending ? (
                      <tr>
                        <td colSpan={13} className="text-center py-8">
                          <LoadingComponent />
                        </td>
                      </tr>
                    ) : devices.length === 0 ? (
                      <tr>
                        <td colSpan={13} className="text-center py-8">
                          <NoData message="No devices found." />
                        </td>
                      </tr>
                    ) : (
                      devices.map((device, index) => (
                        <tr
                          key={device.id}
                          className={`divide-y divide-gray-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-gray-100"
                          }`}
                        >
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <div className="mt-1 text-black">
                              {device.unique_id}
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              {brands && (
                                <EditableSelectCell
                                  value={device.device_brand_id}
                                  displayValue={device.brand}
                                  id={device.id}
                                  options={brands}
                                  field="brand"
                                  onSave={(data) => {
                                    editCellMutation.mutate({
                                      deviceId: data.id,
                                      field: "device_brand_id",
                                      value: data.value,
                                    });
                                  }}
                                />
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            {models && (
                              <EditableSelectCell
                                value={device.device_model_id}
                                displayValue={device.model}
                                id={device.id}
                                options={models.filter(
                                  (model) =>
                                    model.device_brand_id ===
                                    device.device_brand_id
                                )}
                                field="device_model_id"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "device_model_id",
                                    value: data.value,
                                  })
                                }
                              />
                            )}
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditSerialNumberCell
                              value={device.serial_no || "—"}
                              displayValue={device.serial_no || "—"}
                              id={device.id}
                              field="serial_no"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "serial_no",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={device.counter_black || "—"}
                              displayValue={device.counter_black || "—"}
                              id={device.id}
                              field="counter_black"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "counter_black",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={device.counter_color || "—"}
                              displayValue={device.counter_color || "—"}
                              id={device.id}
                              field="counter_color"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "counter_color",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={device.total_counter || "—"}
                              displayValue={device.total_counter || "—"}
                              id={device.id}
                              field="total_counter"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "total_counter",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={device.stock ?? "—"}
                              displayValue={device.stock ?? "—"}
                              id={device.id}
                              field="stock"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "stock",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={device.palette || "—"}
                              displayValue={device.palette || "—"}
                              id={device.id}
                              field="palette"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "palette",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            {dayjs.utc(device.created_at).format("DD-MM-YYYY")}
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableOptionsCell
                              deviceId={device.id}
                              deviceOptions={device.device_options}
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={device.comment || ""}
                              id={device.id}
                              field="comment"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "comment",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <ActionButtons
                              id={device.id}
                              onDelete={() => handleDelete(device.id)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationV2
                current_page={paginationData.current_page}
                last_page={paginationData.last_page}
                next_page_url={paginationData.next_page_url}
                prev_page_url={paginationData.prev_page_url}
              />

              {showModal && (
                <QReaderDeviceList onClose={() => setShowModal(false)} />
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}
